import { getAuthHeaders } from "../utils/servicesUtils";

// Login Service: Authenticates a user with email and password
export const loginService = async (email, password, mobile) => {
  const response = await fetch("/api/auth/login/", {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, password, mobile }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Login failed");
  }
  return response.json();
};

// Extra accounts service: Gets the account change options for the user
export const availableAccountsForUser = async () => {
  const response = await fetch("/api/auth/mint-demo-roles/", {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Fetch for available accounts failed");
  }
  return response.json();
};

// Authentication Selection Service: Defines the auth method that the user will use for their login attempt
export const authMethodSelector = async (email, method, reset_password = false) => {
  const queryParams = new URLSearchParams({
    email,
    method,
    reset_password,
  });
  const response = await fetch(`/api/auth/auth-method/?${queryParams.toString()}`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to select the authentication method");
  }
  return response.json();
};

// 2FA Authenticator Setup Service: Sets up the authenticator by verifying it with a code
export const setupTOTPService = async (email, totpInitialCode) => {
  const response = await fetch("/api/auth/setup-totp/", {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, totp_code: totpInitialCode }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "TOTP setup failed");
  }
  return response.json();
};

// 2FA Authenticator Reset service: Removes the current authenticator from the user's account
export const resetTOTP = async (userId) => {
  const ENDPOINT = `/api/auth/reset/totp/${encodeURIComponent(userId)}/`;
  try {
    const response = await fetch(ENDPOINT, {
      method: "POST",
      headers: getAuthHeaders()
    });
    const responseData = await response.json();
    if (!response.ok) {
      throw responseData;
    }
    return responseData;
  } catch (error) {
    console.error("Error in resetTOTP:", error);
    if (error instanceof TypeError) {
      throw new Error("A connection error occurred while trying resetTOTP");
    }
    throw error;
  }
};

// TOTP Verification Service: Verifies the TOTP code during authentication using an authenticator app
export const verifyTOTPService = async (email, totpCode, reset_password = false, remember = false) => {
  const queryParams = new URLSearchParams({
    reset_password,
    remember
  });
  const response = await fetch(`/api/auth/verify-totp/?${queryParams.toString()}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, totp_code: totpCode }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "TOTP verification failed");
  }
  return response.json();
};

// Email Code Verification Service: Verifies the code sent to a user's email during authentication using an email address
export const verifyEmailCodeService = async (email, code, reset_password = false, remember = false) => {
  const queryParams = new URLSearchParams({
    email,
    code,
    reset_password,
    remember
  });
  const response = await fetch(`/api/auth/email-code/?${queryParams.toString()}`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "TOTP verification failed");
  }
  return response.json();
};

// TOTP Verification for Password Reset Service: Verifies the TOTP code when resetting password
export const verifyTOTPForResetService = async (email, totpCode) => {
  const queryParams = new URLSearchParams({ reset_password: "true" });
  const response = await fetch(`/api/auth/verify-totp/?${queryParams.toString()}`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ email, totp_code: totpCode }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "TOTP reset verification failed");
  }
  return response.json();
};

export const requestNewEmailVerificationCodeService = async () => {

};

/**
 * Single dedicated refresh-token service.
 * All refresh calls must go through this function — no ad-hoc fetch elsewhere.
 * @param {string} sessionId
 * @param {string} currentToken
 * @returns {Promise<{ access_token: string }>}
 */
export const refreshTokenService = async (sessionId, currentToken) => {
  const response = await fetch(
    `/api/services/auth/refresh-token/?sessionId=${encodeURIComponent(sessionId)}`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${currentToken}` },
    }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Token refresh failed");
  }
  return response.json();
};